const FADE_DURATION_MS = 100;
const FADE_EASING = 'ease-out';
const SAFETY_TIMEOUT_MS = 400;
const READIUM_IFRAME_UPDATE_DELAY_MS = 300;

/**
 * Generic fade animator for Readium Fixed Layout EPUBs.
 * Handles the common pattern: fade out -> apply changes -> wait for Readium -> fade in.
 *
 * Used by:
 * - SidebarViewportAnimator: when sidebar opens/closes
 * - Readium2: when layout changes (single/double)
 */
export class ReadiumFadeAnimator {
    constructor() {
        this._cleanup = null;
    }

    /**
     * Animates a change with fade out/in effect.
     * @param {HTMLElement} spineElement - The Readium spineElement to animate
     * @param {Function} onApplyChanges - Callback to apply changes while faded out
     * @param {Function} [onComplete] - Callback when animation completes
     */
    animate(spineElement, onApplyChanges, onComplete = () => {}) {
        if (!spineElement) {
            onApplyChanges();
            onComplete();
            return;
        }

        this.cancel();

        let safetyTimeout = null;
        let fadeInSafetyTimeout = null;

        const fadeIn = () => {
            spineElement.style.transition = `opacity ${FADE_DURATION_MS}ms ${FADE_EASING}`;
            spineElement.offsetHeight;
            spineElement.style.opacity = '1';

            const onFadeInEnd = () => {
                clearTimeout(fadeInSafetyTimeout);
                spineElement.removeEventListener('transitionend', onFadeInEnd);
                spineElement.style.transition = '';
                spineElement.style.opacity = '';
                this._cleanup = null;
                onComplete();
            };

            spineElement.addEventListener('transitionend', onFadeInEnd, { once: true });

            fadeInSafetyTimeout = setTimeout(() => {
                spineElement.removeEventListener('transitionend', onFadeInEnd);
                onFadeInEnd();
            }, SAFETY_TIMEOUT_MS);
        };

        const onFadeOutEnd = async () => {
            clearTimeout(safetyTimeout);
            spineElement.removeEventListener('transitionend', onFadeOutEnd);

            await onApplyChanges();

            setTimeout(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        fadeIn();
                    });
                });
            }, READIUM_IFRAME_UPDATE_DELAY_MS);
        };

        spineElement.style.transition = `opacity ${FADE_DURATION_MS}ms ${FADE_EASING}`;
        spineElement.offsetHeight;
        spineElement.style.opacity = '0';

        spineElement.addEventListener('transitionend', onFadeOutEnd, { once: true });

        safetyTimeout = setTimeout(() => {
            spineElement.removeEventListener('transitionend', onFadeOutEnd);
            onFadeOutEnd();
        }, SAFETY_TIMEOUT_MS);

        this._cleanup = () => {
            clearTimeout(safetyTimeout);
            clearTimeout(fadeInSafetyTimeout);
            spineElement.removeEventListener('transitionend', onFadeOutEnd);
            spineElement.style.transition = '';
            spineElement.style.opacity = '';
        };
    }

    /**
     * Cancels any pending animation and cleans up.
     */
    cancel() {
        if (this._cleanup) {
            this._cleanup();
            this._cleanup = null;
        }
    }
}
